import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/market-cars.json";
import { QueryParams } from "./types";
import { getDataFilteredAndSorted } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { PaginatedList } from "@/types/entity";
import type { MarketCar } from "@/types/market";

if (import.meta.env.MODE === "test") {
  // Mocking the API response for watchlists
  const mock = AxiosMock.getInstance();
  const uri = new RegExp("/carburate/market/decades/.+/cars/.+");
  mock.onGet(uri).reply(200, data);
}

type DecadeQueryParams = QueryParams & {
  decade: string;
};

export const getMarketDecadeCars = ({ decade, page, pageSize }: DecadeQueryParams) => {
  return api.get(
    `/carburate/market/decades/${decade}/cars/?page=${page}&page_size=${pageSize}`
  ) as Promise<PaginatedList<MarketCar>>;
};

export const getMarketDecadeCarsQueryOptions = ({ decade, page, pageSize }: DecadeQueryParams) => {
  return queryOptions({
    queryKey: ["market", "decades", decade, "cars", { page, pageSize }],
    queryFn: () => getMarketDecadeCars({ decade, page, pageSize }),
  });
};

type UseMarketDecadeCarsOptions = DecadeQueryParams & {
  enabled?: boolean;
  queryConfig?: QueryConfig<typeof getMarketDecadeCars>;
};

export const useMarketDecadeCars = ({
  decade,
  parameters,
  enabled = true,
  queryConfig,
  page,
  pageSize,
  sort,
}: UseMarketDecadeCarsOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<MarketCar>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && !!decade && enabled,
    ...getMarketDecadeCarsQueryOptions({ decade, page: currentPage, pageSize: currentPageSize }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    data = getDataFilteredAndSorted(data, { parameters, page, pageSize, sort });
  }

  return { data, isSuccess, ...props };
};
